import React, { useState, useEffect } from "react";
import { Draggable } from "react-beautiful-dnd";
import Editor from "../../components/common/Editor";
import "./ActionCard.css";
import EROIcon from "../../assets/Icons/lifejacket.svg";
import FireFighterIcon from "../../assets/Icons/redlight.png";
import ParamedicIcon from "../../assets/Icons/yellowsirene.svg";
import PoliceIcon from "../../assets/Icons/azurebluesirene.png";
import CrisiTeamIcon from "../../assets/Icons/crisisteam.png";
import Select from "react-select";
import config from "./../../config/config.json";
import http from "./../../services/httpService";

const OptionWithAvatar = ({ innerProps, label, data }) => (
  <div {...innerProps} style={{ marginBottom: 5 }}>
    {data.icon && (
      <img
        src={data.icon}
        alt="Icon"
        style={{
          marginRight: "8px",
          borderRadius: "50%",
          width: "24px",
          height: "24px",
        }}
      />
    )}
    {label}
  </div>
);

const SingleValueWithAvatar = ({ innerProps, children, data }) => (
  <div {...innerProps} style={{ marginBottom: 5 }}>
    {data.icon && (
      <img
        src={data.icon}
        alt="Icon"
        style={{
          marginRight: "8px",
          borderRadius: "50%",
          width: "24px",
          height: "24px",
        }}
      />
    )}
    {children}
  </div>
);

const getAssigneeTypeIcon = (type) => {
  switch (type) {
    case "ERO":
      return EROIcon;
    case "FireFighter":
      return FireFighterIcon;
    case "PoliceOfficer":
      return PoliceIcon;
    case "Paramedic":
      return ParamedicIcon;
    case "CRTMember":
      return CrisiTeamIcon;
    default:
      return null;
  }
};

const getEROs = () => {
  return http.get(config.apiUrl + "/eros");
};

const getPoliceOfficers = () => {
  return http.get(config.apiUrl + "/policeofficers");
};

const getFireFighters = () => {
  return http.get(config.apiUrl + "/firefighters");
};

const getParamedics = () => {
  return http.get(config.apiUrl + "/paramedics");
};

const getCRTMembers = () => {
  return http.get(config.apiUrl + "/crtmembers");
};

const ActionCard = ({ action, index, onUpdate, onDelete, users = [] }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedAction, setEditedAction] = useState(action);
  const [isSaving, setIsSaving] = useState(false);
  const [assigneeOptions, setAssigneeOptions] = useState([]);
  const [loadingAssignees, setLoadingAssignees] = useState(false);

  const assigneeTypes = [
    { value: "FireFighter", label: "Fire Fighter", icon: FireFighterIcon },
    { value: "PoliceOfficer", label: "Police Officer", icon: PoliceIcon },
    { value: "Paramedic", label: "Paramedic", icon: ParamedicIcon },
    { value: "ERO", label: "ERO", icon: EROIcon },
    { value: "CRTMember", label: "CRT Member", icon: CrisiTeamIcon },
  ];

  const [assigneePairs, setAssigneePairs] = useState(() => {
    const pairs = [];
    const assignees = action.assignee || [];
    const assigneeTypes = action.assigneeType || [];

    for (let i = 0; i < Math.max(assignees.length, assigneeTypes.length); i++) {
      pairs.push({
        type: assigneeTypes[i] || "ERO",
        assignee: assignees[i] || null,
      });
    }

    if (pairs.length === 0) {
      pairs.push({ type: "ERO", assignee: null });
    }

    return pairs;
  });

  useEffect(() => {
    setEditedAction(action);
  }, [action]);

  const fetchAssignees = async () => {
    const uniqueTypes = [...new Set(assigneePairs.map((pair) => pair.type))];
    if (uniqueTypes.length === 0) {
      setAssigneeOptions([]);
      return;
    }

    setLoadingAssignees(true);
    try {
      const allAssignees = [];

      for (const type of uniqueTypes) {
        let apiCall = null;

        switch (type) {
          case "ERO":
            apiCall = getEROs;
            break;
          case "PoliceOfficer":
            apiCall = getPoliceOfficers;
            break;
          case "FireFighter":
            apiCall = getFireFighters;
            break;
          case "Paramedic":
            apiCall = getParamedics;
            break;
          case "CRTMember":
            apiCall = getCRTMembers;
            break;
          default:
            continue;
        }

        try {
          const response = await apiCall();
          if (response.data) {
            const formattedData = response.data.map((item) => ({
              value: item._id,
              label: `${item.user?.contactName?.first} ${item.user?.contactName?.last}`,
              icon: item.user?.imageSrc || getAssigneeTypeIcon(type),
              type: type,
            }));
            allAssignees.push(...formattedData);
          }
        } catch (error) {
          console.error(`Error fetching ${type}:`, error);
        }
      }

      setAssigneeOptions(allAssignees);
    } catch (error) {
      console.error("Error fetching assignees:", error);
    } finally {
      setLoadingAssignees(false);
    }
  };

  useEffect(() => {
    fetchAssignees();
  }, [assigneePairs]);

  const addAssigneePair = () => {
    setAssigneePairs((prev) => [...prev, { type: "ERO", assignee: null }]);
  };

  const updatePairType = (pairIndex, newType) => {
    setAssigneePairs((prev) => {
      const newPairs = [...prev];
      newPairs[pairIndex] = {
        ...newPairs[pairIndex],
        type: newType,
        assignee: null,
      };
      return newPairs;
    });
  };

  const updatePairAssignee = (pairIndex, assigneeId) => {
    setAssigneePairs((prev) => {
      const newPairs = [...prev];
      newPairs[pairIndex] = { ...newPairs[pairIndex], assignee: assigneeId };
      return newPairs;
    });
  };

  const removePair = (pairIndex) => {
    setAssigneePairs((prev) => prev.filter((_, index) => index !== pairIndex));
  };

  const prepareAssigneeData = () => {
    const assignee = [];
    const assigneeType = [];

    assigneePairs.forEach((pair) => {
      if (pair.assignee) {
        assignee.push(pair.assignee);
        assigneeType.push(pair.type);
      }
    });

    return { assignee, assigneeType };
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const { assignee, assigneeType } = prepareAssigneeData();

      const actionToSave = {
        _id: action._id,
        name: editedAction.name || "",
        step: action.step || (index + 1).toString(),
        assigneeType:
          assigneeType.length >= 1 && assignee.length >= 1 ? assigneeType : [],
        assignee:
          assigneeType.length >= 1 && assignee.length >= 1 ? assignee : [],
      };

      await onUpdate(actionToSave);
      setIsEditing(false);
    } catch (error) {
      console.error("Error saving action:", error);
      alert("Failed to save action: " + (error.message || error));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedAction(action);
    setAssigneePairs(() => {
      const pairs = [];
      const assignees = action.assignee || [];
      const assigneeTypes = action.assigneeType || [];

      for (
        let i = 0;
        i < Math.max(assignees.length, assigneeTypes.length);
        i++
      ) {
        pairs.push({
          type: assigneeTypes[i] || "ERO",
          assignee: assignees[i] || null,
        });
      }

      if (pairs.length === 0) {
        pairs.push({ type: "ERO", assignee: null });
      }

      return pairs;
    });
    setIsEditing(false);
  };

  const handleChange = (field, value) => {
    setEditedAction((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete "${action.name}"?`)) {
      onDelete(action._id);
    }
  };

  const renderAssigneeFields = () => (
    <div className="form-group">
      <label>Assignees:</label>
      {assigneePairs.map((pair, pairIndex) => (
        <div
          key={pairIndex}
          className="assignee-pair"
          style={{ marginBottom: "10px" }}
        >
          <div style={{ display: "flex", gap: "10px", marginBottom: "5px" , alignItems:"center"}}>
            <Select
              options={assigneeTypes}
              value={assigneeTypes.find((t) => t.value === pair.type)}
              onChange={(selected) => updatePairType(pairIndex, selected.value)}
              components={{
                Option: OptionWithAvatar,
                SingleValue: SingleValueWithAvatar,
              }}
              className="flex-grow-1"
            />
            <button
              onClick={() => removePair(pairIndex)}
              className="delete-btn"
              style={{ padding: "0 10px" }}
            >
              ❌
            </button>
          </div>
          <Select
            options={assigneeOptions.filter((opt) => opt.type === pair.type)}
            value={assigneeOptions.find((opt) => opt.value === pair.assignee)}
            onChange={(selected) =>
              updatePairAssignee(pairIndex, selected?.value || null)
            }
            components={{
              Option: OptionWithAvatar,
              SingleValue: SingleValueWithAvatar,
            }}
            placeholder="Select assignee..."
            className="w-100"
            isClearable
          />
        </div>
      ))}
      <button onClick={addAssigneePair} className="action-step">
        + Add Assignee
      </button>
    </div>
  );

  const renderAssignedUsers = () => {
    if (assigneePairs.every((pair) => !pair.assignee)) {
      return <span className="assignee-value">Unassigned</span>;
    }

    return (
      <div className="assigned-users">
        {assigneePairs.map((pair, idx) => {
          if (!pair.assignee) return null;

          const assigneeData = assigneeOptions.find(
            (opt) => opt.value === pair.assignee
          );
          const typeData = assigneeTypes.find((t) => t.value === pair.type);
          return (
            <div
              key={idx}
              className="assignee-item"
              style={{ display: "flex", marginBottom: 10, alignItems:"center" }}
            >
              {typeData && (
                <>
                  <img
                    src={typeData.icon}
                    style={{ width: 20, height: 20, marginRight: 3 }}
                    alt=""
                  />
                  <span>{typeData.label}</span>
                </>
              )}
              {assigneeData ? (
                <div>
                  <img
                    src={assigneeData.icon}
                    style={{
                      width: 24,
                      height: 24,
                      marginRight: 5,
                      borderRadius: "50%",
                      marginLeft: 19,
                    }}
                    alt=""
                  />
                  <span>{assigneeData.label}</span>
                </div>
              ) : (
                <span style={{ marginLeft: 10 }}>none</span>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Draggable draggableId={action._id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className={`action-card ${snapshot.isDragging ? "dragging" : ""}`}
          onDoubleClick={() => setIsEditing(true)}
        >
          <div className="action-card-header">
            <div className="action-step">Step {action.step || index + 1}</div>
            <div className="action-controls">
              {!isEditing ? (
                <>
                  <button
                    className="edit-btn"
                    onClick={() => setIsEditing(true)}
                    title="Edit Action"
                  >
                    ✏️
                  </button>
                  <button
                    className="delete-btn"
                    onClick={handleDelete}
                    title="Delete Action"
                  >
                    🗑️
                  </button>
                </>
              ) : (
                <div className="edit-controls">
                  <button
                    className="save-btn"
                    onClick={handleSave}
                    disabled={isSaving}
                    title="Save"
                  >
                    {isSaving ? "⏳" : "✅"}
                  </button>
                  <button
                    className="cancel-btn"
                    onClick={handleCancel}
                    title="Cancel"
                  >
                    ❌
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="action-card-body">
            {isEditing ? (
              <div className="edit-form">
                <div className="form-group">
                  <label>Action:</label>
                  <Editor
                    initialContent={editedAction.name || ""}
                    onChangeHandler={(newText) => handleChange("name", newText)}
                  />
                </div>

                {renderAssigneeFields()}
              </div>
            ) : (
              <div className="action-content">
                <Editor
                  initialContent={action.name || "Unnamed Action"}
                  readOnly={true}
                />

                <div className="action-assignee">
                  <span className="assignee-label">Assigned to:</span>
                  {renderAssignedUsers()}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </Draggable>
  );
};

export default ActionCard;
